import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import http from '@/lib/http'
import type { Flag, ParsedQuestionnaire } from '@/types/data'
import { useGetFlags } from '@/services/flags'
import * as Sentry from '@sentry/react'

export type ReasoningTemplate = {
  name: string
  text: string
}

export type Questionnaire = {
  hash: string
  title: string
  type?: 'treatment' | 'review' | 'care' | 'fan'
  questions_count: number
  updated_at: string
  tags: string[]
  reasoning_templates: {
    prescriber_justification: ReasoningTemplate[] | null
    on_hold_reasons: ReasoningTemplate[] | null
    rejection_reasons: ReasoningTemplate[] | null
  }
  batch_approval: boolean
}

const BATCH_FLAGS = [
  '7c356d8d4f0a5ff90a4089397582bd', // staging/dev
  'bd00417aa21f4e5c4c2b323b44706f' // prod
]

export function parseQuestionnaire(str: string, flagsMap: Record<string, Flag>) {
  const cleanedJsonString = str.replace(/[\n\r\t]/g, '') // Remove newlines, carriage returns, and tabs
  let result = null

  try {
    const parsed = JSON.parse(cleanedJsonString)
    // v2 questionnaire is object
    result = Array.isArray(parsed) ? parsed : Object.values(parsed)
  } catch (error) {
    result = [
      {
        title: 'Error parsing questionnaire',
        description: 'The questionnaire JSON could not be parsed. Please check the format!!!'
      }
    ]
    console.error('Error parsing questionnaire JSON:', error)
    Sentry.captureMessage('Error parsing questionnaire JSON', {
      level: 'error',
      contexts: {
        questionnaire: {
          raw: str
        }
      }
    })
  }

  // @ts-expect-error use any for now
  const parseLinksAndHasFlags = (obj: any) => {
    if (Array.isArray(obj)) {
      return obj.map(parseLinksAndHasFlags)
    }

    if (obj?.flags?.length > 0) {
      const flags = obj.flags as string[]
      const validFlags = flags.filter((flag: string) => flagsMap[flag] && !BATCH_FLAGS.includes(flag))
      const hasSomeNestedBatchFlag = BATCH_FLAGS.some(entry =>
        // @ts-ignore
        obj?.options?.some(opt =>
          // @ts-ignore
          opt?.logic?.flags?.some(flag => flag.indexOf(entry) > -1)
        )
      )

      obj.hasFlags = !!validFlags.length && !hasSomeNestedBatchFlag
      obj.isBatch =
        flags.some(flag => BATCH_FLAGS.includes(flag)) || hasSomeNestedBatchFlag || (obj?.tags ?? []).includes('batch')
    }

    if (obj?.title?.includes('Provide picture') || obj?.type === 'uploader') {
      // Handle both old format (single string) and new format (array of strings)
      if (Array.isArray(obj.answer)) {
        obj.links = obj.answer
        obj.link = obj.answer[0] || ''
      } else {
        obj.link = `https://workdrive.zohoexternal.com/external/${obj.answer}`
      }
      obj.imageUploaded = true
    }

    return obj
  }

  result = parseLinksAndHasFlags(result)

  return result as ParsedQuestionnaire[]
}

const mapTemplateToOptions = (templates: ReasoningTemplate[]) => {
  if (!templates?.length) return []
  return templates.map(template => ({
    label: template.name,
    value: template.text
  }))
}

const mapTemplatesIntoArrays = (parsedQuestions: ParsedQuestionnaire[], allQuestions: Questionnaire[]) => {
  const approvalTemplates: ReasoningTemplate[] = []
  const rejectionTemplates: ReasoningTemplate[] = []
  const onHoldTemplates: ReasoningTemplate[] = []

  if (!allQuestions || parsedQuestions.length === 0) {
    return { approvalTemplates, rejectionTemplates, onHoldTemplates }
  }

  const questionnaireHash = parsedQuestions[0]?.questionnaire

  const item = allQuestions.find((q: Questionnaire) => q.hash === questionnaireHash)

  const reasoningTemplates = item?.reasoning_templates

  if (reasoningTemplates?.prescriber_justification)
    approvalTemplates.push(...reasoningTemplates.prescriber_justification)
  if (reasoningTemplates?.on_hold_reasons) onHoldTemplates.push(...reasoningTemplates.on_hold_reasons)
  if (reasoningTemplates?.rejection_reasons) rejectionTemplates.push(...reasoningTemplates.rejection_reasons)

  return { approvalTemplates, rejectionTemplates, onHoldTemplates }
}

export const useQuestionnaire = (questionaireJson: string) => {
  const { data: flagsMap } = useGetFlags()
  const parsedQuestions = useMemo(() => {
    if (!questionaireJson || !flagsMap) return []
    return parseQuestionnaire(questionaireJson, flagsMap)
  }, [questionaireJson, flagsMap])

  const { data } = useQuery({
    queryKey: ['questionnaireData'],
    queryFn: () => http.get<Questionnaire[], Questionnaire[]>('/prescriber/questionnaires'),
    staleTime: 1000 * 60 * 60,
    select: data => {
      const mappedTemplates = mapTemplatesIntoArrays(parsedQuestions, data)
      const approvalOptions = mapTemplateToOptions(mappedTemplates.approvalTemplates)
      const rejectionOptions = mapTemplateToOptions(mappedTemplates.rejectionTemplates)
      const onHoldOptions = mapTemplateToOptions(mappedTemplates.onHoldTemplates).map(option => ({
        ...option,
        group: 'Email'
      }))
      const fanOptions = data
        .filter(questionnaire => questionnaire.type === 'fan')
        .map(questionnaire => ({ label: questionnaire.title, value: `fan:${questionnaire.hash}`, group: 'FAN' }))

      return { approvalOptions, rejectionOptions, onHoldOptions, fanOptions }
    }
  })

  return { parsedQuestions, templateOptions: data }
}
